/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The Call Console: who is calling, and everything open on their account,
 * beside the call controls.
 *
 * Three zones. A strip across the top for the agent's own status and their
 * queues, a phone column on the left, and customer context on the right.
 *
 * The strip exists because the console previously showed an agent nothing
 * about themselves. Presence lived only in the dock, device registration was
 * invisible, and queue depth was nowhere -- so an empty screen could mean a
 * quiet morning or a softphone that silently deregistered twenty minutes ago,
 * and there was no way to tell which.
 *
 * Layout is rail-left / context-right rather than the order-detail convention
 * of rail-right. Here the *call* is the subject and everything beside it is
 * supporting evidence; putting the rail left also means it never reflows when
 * the context tabs change height mid-call.
 *
 * The rail is sticky and self-scrolling via scaledVh, which is required rather
 * than cosmetic: appScale applies `body { zoom: 0.8 }` above 992px, so a raw
 * 100vh on an in-flow element overshoots the viewport by 25%.
 *
 * While mounted, this registers as the telephony inline host and the dock
 * shrinks to a pill -- one engine, one visible set of controls.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  CircularProgress,
  Grid,
  Icon,
  Snackbar,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import {
  Business,
  ChevronRight,
  Person,
  PersonSearch,
  Search,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MonoText from "components/MonoText";
import StatusBadge from "components/StatusBadge";
import InfoCard from "components/detail/InfoCard";
import DetailField from "components/detail/DetailField";
import MiniTable from "components/detail/MiniTable";
import ConversationRecording from "components/CallRecording/ConversationRecording";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { scaledVh } from "assets/theme/base/appScale";
import { useAuth } from "auth/Auth";
import { useTravelUi } from "hooks/useTravelUi";
import { useTelephony } from "telephony/TelephonyContext";
import colors from "styles/tokens/colors";
import { getApiErrorMessage } from "utils/apiErrors";
import AgentStrip from "./AgentStrip";
import CallPanel from "./CallPanel";
import CallConsoleV2 from "./v2/CallConsoleV2";
import {
  PARENT_TABLES,
  callParent,
  emptyFollowUp,
  followUpActivity,
  followUpProblem,
} from "./followUp";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const cc = colors.callCenter;

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "deals", label: "Deals" },
  { key: "orders", label: "Orders", legacyCrm: true },
  { key: "quotations", label: "Quotes", legacyCrm: true },
  { key: "cases", label: "Cases" },
  { key: "conversations", label: "History" },
];

const MATCH_ICON = {
  contact: <Person fontSize="small" />,
  account: <Business fontSize="small" />,
  lead: <PersonSearch fontSize="small" />,
};

/** Rows the agent picks from when a number matches more than one record. */
function MatchPicker({ matches, onPick }) {
  return (
    <MDBox display="flex" flexDirection="column" gap={1}>
      {matches.map((m) => (
        <MDBox
          key={`${m.type}-${m.id}`}
          onClick={() => onPick(m)}
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{
            p: 1.5,
            borderRadius: "8px",
            border: "1px solid #E8EDF5",
            cursor: "pointer",
            "&:hover": { bgcolor: "#FAFBFD" },
          }}
        >
          <MDBox sx={{ color: cc.muted, display: "flex" }}>
            {MATCH_ICON[m.type] || <Person fontSize="small" />}
          </MDBox>
          <MDBox flexGrow={1} minWidth={0}>
            <MDTypography variant="button" fontWeight="bold" color="dark" display="block">
              {m.name}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {[m.type, m.subtitle].filter(Boolean).join(" · ")}
              {m.seen_before ? " · spoken before" : ""}
            </MDTypography>
          </MDBox>
          <ChevronRight sx={{ color: cc.muted }} />
        </MDBox>
      ))}
    </MDBox>
  );
}

export default function CallConsole() {
  const { axiosInstance, user } = useAuth();
  const isV2 = useTheme()?.pearlUi === "v2";
  const { showTravel } = useTravelUi();
  const visibleTabs = TABS.filter((tab) => !(showTravel && tab.legacyCrm));
  const [searchParams] = useSearchParams();
  const {
    enabled,
    settingsChecked,
    callState,
    activeCall,
    durationSec,
    muted,
    placeCall,
    adoptConversation,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
    sendDigits,
    submitDisposition,
    skipWrapUp,
    notesDraft,
    saveNotesDraft,
    setInlineHostActive,
    isLeader,
    presence,
    changePresence,
    registered,
    micError,
  } = useTelephony();

  const [activeTab, setActiveTab] = useState("overview");
  useEffect(() => {
    if (showTravel && (activeTab === "orders" || activeTab === "quotations")) {
      setActiveTab("overview");
    }
  }, [showTravel, activeTab]);
  const [lookup, setLookup] = useState(null);
  const [looking, setLooking] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [manualNumber, setManualNumber] = useState("");
  const [dispositions, setDispositions] = useState([]);
  const [dispositionId, setDispositionId] = useState("");
  const [notes, setNotes] = useState("");
  const [recent, setRecent] = useState([]);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Which past call the agent opened in the Previous calls tab.
  const [selectedCallId, setSelectedCallId] = useState(null);
  // Optional follow-up task (with reminder) filed alongside the outcome.
  const [followUp, setFollowUp] = useState(() => emptyFollowUp(null));
  const [users, setUsers] = useState([]);
  // Calls this console has already attached to their single matching record.
  const autoLinkedRef = useRef(new Set());

  // Claim inline host: the dock collapses to a pill while this is mounted.
  useEffect(() => {
    setInlineHostActive(true);
    return () => setInlineHostActive(false);
  }, [setInlineHostActive]);

  useEffect(() => {
    if (!enabled) return;
    axiosInstance
      // Agent-facing list: needs only the Call Center seat, not the queues grant.
      .get("/telephony/dispositions")
      .then((r) => setDispositions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDispositions([]));
    axiosInstance
      .get("/call-console/recent", { params: { limit: 10 } })
      .then((r) => setRecent(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRecent([]));
    axiosInstance
      .get("/users/")
      .then((r) => setUsers(Array.isArray(r.data) ? r.data.filter((u) => u.is_active !== false) : []))
      .catch(() => setUsers([]));
  }, [axiosInstance, enabled]);

  const runLookup = useCallback(
    async (phone) => {
      if (!phone) return;
      setLooking(true);
      setError("");
      try {
        const res = await axiosInstance.get("/call-console/lookup", { params: { phone } });
        setLookup(res.data);
        // Only auto-select when there is nothing to choose between.
        setResolved(res.data.resolved || null);
        setActiveTab("overview");
        // A different customer now: an expanded recording from the last one
        // would otherwise sit there under the new caller's history.
        setSelectedCallId(null);
      } catch (err) {
        setError(getApiErrorMessage(err, "Lookup failed."));
      } finally {
        setLooking(false);
      }
    },
    [axiosInstance]
  );

  // Restore any note typed for this call before a reload or route change.
  useEffect(() => {
    if (activeCall?.id && notesDraft[activeCall.id] !== undefined) {
      setNotes(notesDraft[activeCall.id]);
    }
  }, [activeCall?.id, notesDraft]);

  // Screen-pop: whenever a call arrives, look up whoever is on it.
  const counterparty =
    activeCall?.direction === "outbound" ? activeCall?.to_address : activeCall?.from_address;

  useEffect(() => {
    if (counterparty) runLookup(counterparty);
  }, [counterparty, runLookup]);

  // Deep link from the dock: ?call=<id> reopens a call's context.
  //
  // This binds the call as well as populating the context pane. It used to only
  // do the latter, which made a reloaded console look healthy -- the customer's
  // record was right there -- while every control that needs `activeCall`
  // (linking the call, saving wrap-up) stayed inert.
  useEffect(() => {
    const callId = searchParams.get("call");
    if (!callId || activeCall) return;
    adoptConversation(callId).then((row) => {
      if (!row) return;
      const phone = row.direction === "outbound" ? row.to_address : row.from_address;
      if (phone) runLookup(phone);
    });
  }, [searchParams, activeCall, adoptConversation, runLookup]);

  const linkRecord = async (match, { quiet = false } = {}) => {
    setResolved(match);
    if (!activeCall) {
      // The match still drives the context pane, but there is no call to
      // attach it to. Say so rather than appearing to have saved.
      setError("No active call to attach this record to.");
      return;
    }
    const parentTable = PARENT_TABLES[match.type];
    if (!parentTable) return;
    try {
      await axiosInstance.post(`/call-console/conversations/${activeCall.id}/link`, {
        parent_table: parentTable,
        parent_id: match.id,
        account_id: match.type === "account" ? match.id : match.account_id || null,
        contact_id: match.type === "contact" ? match.id : match.contact_id || null,
        lead_id: match.type === "lead" ? match.id : null,
        deal_id: match.type === "deal" ? match.id : null,
        case_id: match.type === "case" ? match.id : null,
      });
      if (!quiet) setToast(`Call linked to ${match.name}.`);
      return true;
    } catch (err) {
      if (!quiet) setError(getApiErrorMessage(err, "Could not link the call."));
      return false;
    }
  };

  // When the number matches exactly one record, attach the call to it. The
  // console used to show that record without telling the server, so the saved
  // outcome landed on no one's timeline.
  useEffect(() => {
    const id = activeCall?.id;
    if (!id || !resolved || activeCall?.primary_parent_table) return;
    if (autoLinkedRef.current.has(id)) return;
    autoLinkedRef.current.add(id);
    linkRecord(resolved, { quiet: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall?.id, activeCall?.primary_parent_table, resolved]);

  // A new call starts with a fresh follow-up, named after whoever it is with.
  useEffect(() => {
    setFollowUp(emptyFollowUp(resolved?.name || activeCall?.caller_name || null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall?.id]);
  useEffect(() => {
    if (resolved?.name) {
      setFollowUp((f) => (f.enabled ? f : { ...f, title: `Follow up with ${resolved.name}` }));
    }
  }, [resolved?.name]);

  const followUpParent = callParent(activeCall, resolved);
  const followUpError = followUpProblem(followUp, followUpParent);

  const onDial = async () => {
    if (!manualNumber.trim()) return;
    setError("");
    try {
      await placeCall({ to: manualNumber.trim() });
    } catch (err) {
      setError(getApiErrorMessage(err, "The call could not be placed."));
    }
  };

  // A link or a newly created record changes who the call is about, so the
  // context pane has to follow it rather than keep showing the old lookup.
  const onRecordLinked = useCallback(
    (updated, message) => {
      setToast(message || "Call linked.");
      if (!updated) return;
      const phone =
        updated.direction === "outbound" ? updated.to_address : updated.from_address;
      if (phone) runLookup(phone);
    },
    [runLookup]
  );

  const onComplete = async () => {
    if (followUpError) {
      setError(followUpError);
      return;
    }
    setSaving(true);
    // Captured before submitDisposition clears the call from the console.
    const parent = followUpParent;
    const wantFollowUp = followUp.enabled && parent;
    const callNotes = notes;
    try {
      const saved = await submitDisposition({ dispositionId: dispositionId || null, notes });
      setDispositionId("");
      setNotes("");
      let message = "Call saved.";
      if (wantFollowUp) {
        const target =
          saved?.primary_parent_table && saved?.primary_parent_id
            ? { table: saved.primary_parent_table, id: saved.primary_parent_id }
            : parent;
        try {
          await axiosInstance.post(
            "/activities/",
            followUpActivity(followUp, target, { userId: user?.id, callNotes })
          );
          message = "Call saved, follow-up added.";
        } catch (err) {
          // The outcome is already saved; say so, and why the task is not.
          setError(getApiErrorMessage(err, "The call was saved, but the follow-up task could not be added."));
        }
      }
      setFollowUp(emptyFollowUp(null));
      setToast(message);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save the call."));
    } finally {
      setSaving(false);
    }
  };

  // Dialling out needs more than a leader tab: an agent who is not Available
  // has no registered Device to originate from. Same rule as CallButton, and
  // the reason is spelled out rather than left as a dead control.
  const canDial = isLeader && registered && presence === "available";
  const dialDisabledReason = !isLeader
    ? "The softphone is active in another browser tab."
    : !registered
    ? "Your softphone is not connected yet."
    : presence !== "available"
    ? "Set yourself Available to place a call."
    : null;

  const onDialRecord = async ({ to, context }) => {
    setError("");
    try {
      await placeCall({ to, context });
    } catch (err) {
      setError(getApiErrorMessage(err, "The call could not be placed."));
    }
  };

  const onSkipWrapUp = () => {
    // The disposition select is cleared but the note draft is not: the call is
    // still dispositionable from its own record, and throwing away what the
    // agent typed mid-call would be the worse half of forcing wrap-up.
    skipWrapUp();
    setDispositionId("");
    setToast("Wrap-up skipped. You are back on available.");
  };

  const related = lookup?.related || {};
  const matches = lookup?.matches || [];
  const needsPick = matches.length > 1 && !resolved;
  const unknown = Boolean(lookup) && matches.length === 0;

  // Render nothing until the settings probe resolves. Rendering the console
  // during the pre-check flashed the "Look up a number" empty state on every
  // mount, which reads as "there is nothing here" in the half-second before
  // the real screen arrives.
  if (!settingsChecked) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!enabled) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={isV2 ? 3 : 6} pb={3} px={isV2 ? 0 : 2}>
          {isV2 && <PageHeader title="Call console" summary="Calls, queues and the caller's records in one place" />}
          <Alert severity="info">
            Telephony is not set up for this organisation yet. An administrator can connect
            Twilio under Settings → Telephony.
          </Alert>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 3 : 4} pb={3} px={isV2 ? 0 : 1}>
        {/* v2 (design-reference "Contact center · Call console"): page title;
            the console below is unchanged. */}
        {isV2 && <PageHeader title="Call console" summary="Calls, queues and the caller's records in one place" />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {isV2 ? (
          <CallConsoleV2
            callState={callState}
            activeCall={activeCall}
            counterparty={counterparty}
            resolvedName={resolved?.name || activeCall?.caller_name}
            durationSec={durationSec}
            muted={muted}
            onAccept={acceptCall}
            onReject={rejectCall}
            onHangUp={hangUp}
            onToggleMute={toggleMute}
            onSendDigits={sendDigits}
            dispositions={dispositions}
            dispositionId={dispositionId}
            onDispositionChange={setDispositionId}
            notes={notes}
            onNotesChange={setNotes}
            onNotesBlur={() => activeCall && saveNotesDraft(activeCall.id, notes)}
            onComplete={onComplete}
            onSkipWrapUp={onSkipWrapUp}
            saving={saving}
            onLinked={onRecordLinked}
            followUp={followUp}
            onFollowUpChange={setFollowUp}
            users={users}
            currentUserId={user?.id ?? null}
            followUpError={followUpError}
            unattached={!followUpParent}
            lookup={lookup}
            resolved={resolved}
            matches={matches}
            onPickMatch={linkRecord}
            looking={looking}
            onLookup={runLookup}
            manualNumber={manualNumber}
            onManualNumberChange={setManualNumber}
            recent={recent}
          />
        ) : (
        <>
        <AgentStrip
          presence={presence}
          onPresenceChange={changePresence}
          registered={registered}
          isLeader={isLeader}
          micError={micError}
          // Queue depth changes the moment a call starts or ends, so refresh
          // on the call state rather than waiting out the poll interval.
          refreshToken={callState}
        />

        <Grid container spacing={3} alignItems="flex-start">
          {/* ------------------------------------------------ call rail */}
          <Grid item xs={12} lg={4}>
            <MDBox
              sx={{
                position: { lg: "sticky" },
                top: 88,
                maxHeight: { lg: scaledVh("140px") },
                overflowY: { lg: "auto" },
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <CallPanel
                callState={callState}
                activeCall={activeCall}
                counterparty={counterparty}
                // The server-resolved name is authoritative until the agent
                // picks a different record; falling back to it means the panel
                // is never blank while the client-side lookup is in flight.
                resolvedName={resolved?.name || activeCall?.caller_name}
                durationSec={durationSec}
                muted={muted}
                manualNumber={manualNumber}
                onManualNumberChange={setManualNumber}
                onDial={onDial}
                onDialRecord={onDialRecord}
                canDial={canDial}
                dialDisabledReason={dialDisabledReason}
                onLookup={runLookup}
                onAccept={acceptCall}
                onReject={rejectCall}
                onHangUp={hangUp}
                onToggleMute={toggleMute}
                onSendDigits={sendDigits}
                dispositions={dispositions}
                dispositionId={dispositionId}
                onDispositionChange={setDispositionId}
                notes={notes}
                onNotesChange={setNotes}
                onNotesBlur={() => activeCall && saveNotesDraft(activeCall.id, notes)}
                onComplete={onComplete}
                onSkipWrapUp={onSkipWrapUp}
                saving={saving}
                onLinked={onRecordLinked}
                followUp={followUp}
                onFollowUpChange={setFollowUp}
                users={users}
                currentUserId={user?.id ?? null}
                followUpError={followUpError}
                unattached={!followUpParent}
              />
            </MDBox>
          </Grid>

          {/* --------------------------------------------- context pane */}
          <Grid item xs={12} lg={8}>
            {looking && (
              <MDBox display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </MDBox>
            )}

            {!looking && needsPick && (
              <InfoCard icon="group" title={`${matches.length} records share this number`}>
                <MDTypography variant="button" color="text" display="block" mb={2}>
                  Choose who you are speaking to.
                </MDTypography>
                <MatchPicker matches={matches} onPick={linkRecord} />
              </InfoCard>
            )}

            {!looking && unknown && (
              <InfoCard icon="phone_missed" title="No matching record">
                <MDTypography variant="button" color="text" display="block" mb={2}>
                  {`Nothing in the CRM matches ${lookup?.phone}.`}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Complete the call, then create a lead from the wrap-up panel.
                </MDTypography>
              </InfoCard>
            )}

            {!looking && !needsPick && !unknown && lookup && (
              <>
                <InfoCard
                  icon="badge"
                  title={resolved?.name || "Caller"}
                  headerAction={
                    matches.length > 1 ? (
                      <MDButton
                        size="small"
                        variant="text"
                        color="info"
                        onClick={() => setResolved(null)}
                      >
                        Wrong person?
                      </MDButton>
                    ) : null
                  }
                >
                  <Grid container spacing={2}>
                    <DetailField label="Number" value={<MonoText variant="button">{lookup.e164 || lookup.phone}</MonoText>} md={4} />
                    <DetailField label="Record type" value={resolved?.type} md={4} />
                    <DetailField label="Detail" value={resolved?.subtitle} md={4} />
                  </Grid>
                </InfoCard>

                {/* MUI Tabs rather than the div-with-a-border-bottom this
                    had: the hand-rolled version was not reachable by keyboard
                    and announced nothing to a screen reader, on a screen an
                    agent drives while talking to someone. */}
                <Tabs
                  value={activeTab}
                  onChange={(_e, v) => setActiveTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mt: 3, mb: 2 }}
                >
                  {visibleTabs.map((tab) => {
                    const count =
                      tab.key === "overview" ? 0 : (related[tab.key] || []).length;
                    return (
                      <Tab
                        key={tab.key}
                        value={tab.key}
                        label={
                          count ? (
                            <Badge
                              badgeContent={count}
                              color="info"
                              sx={{ "& .MuiBadge-badge": { right: -14, top: 4 } }}
                            >
                              {tab.label}
                            </Badge>
                          ) : (
                            tab.label
                          )
                        }
                      />
                    );
                  })}
                </Tabs>

                {activeTab === "overview" && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <InfoCard icon="person" title="Caller">
                        <Grid container spacing={2}>
                          <DetailField label="Name" value={resolved?.name} md={12} />
                          <DetailField label="Email" value={resolved?.email} md={12} />
                          <DetailField
                            label="Phone"
                            value={<MonoText variant="button">{lookup.e164 || lookup.phone}</MonoText>}
                            md={12}
                          />
                        </Grid>
                      </InfoCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoCard icon="insights" title="At a glance">
                        <Grid container spacing={2}>
                          <DetailField label="Open deals" value={String((related.deals || []).length)} md={6} />
                          <DetailField label="Open cases" value={String((related.cases || []).length)} md={6} />
                          {!showTravel && (
                          <DetailField label="Orders" value={String((related.orders || []).length)} md={6} />
                          )}
                          <DetailField
                            label="Previous calls"
                            value={String((related.conversations || []).length)}
                            md={6}
                          />
                        </Grid>
                      </InfoCard>
                    </Grid>

                    {/* This call's own recording and transcript. Mounted only
                        once there is a call to have them: during ringing there
                        is nothing to play, and the recording is not mirrored
                        into our storage until after the conference ends, so in
                        wrap-up this legitimately reads "no recording" for a
                        moment before the pipeline catches up. */}
                    {activeCall?.id && (
                      <Grid item xs={12}>
                        <InfoCard icon="graphic_eq" title="Conversation & Recording">
                          <ConversationRecording conversationId={activeCall.id} />
                        </InfoCard>
                      </Grid>
                    )}
                  </Grid>
                )}

                {activeTab === "deals" && (
                  <InfoCard icon="local_offer" title="Deals">
                    <MiniTable
                      columns={[
                        { key: "name", label: "Deal", width: "2fr" },
                        { key: "stage", label: "Stage" },
                        {
                          key: "value",
                          label: "Value",
                          render: (r) => <MonoText variant="button">{r.value ?? "—"}</MonoText>,
                        },
                      ]}
                      rows={related.deals || []}
                      emptyMessage="No deals for this customer."
                      onRowClick={(r) => window.open(`/deals/${r.id}`, "_blank")}
                    />
                  </InfoCard>
                )}

                {!showTravel && activeTab === "orders" && (
                  <InfoCard icon="shopping_cart" title="Orders">
                    <MiniTable
                      columns={[
                        {
                          key: "order_number",
                          label: "Order",
                          render: (r) => <MonoText variant="button">{r.order_number || r.id}</MonoText>,
                        },
                        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} label={r.status} size="xs" /> },
                        {
                          key: "total_amount",
                          label: "Total",
                          render: (r) => <MonoText variant="button">{r.total_amount ?? "—"}</MonoText>,
                        },
                      ]}
                      rows={related.orders || []}
                      emptyMessage="No orders for this customer."
                      onRowClick={(r) => window.open(`/orders/${r.id}`, "_blank")}
                    />
                  </InfoCard>
                )}

                {!showTravel && activeTab === "quotations" && (
                  <InfoCard icon="request_quote" title="Quotes">
                    <MiniTable
                      columns={[
                        { key: "quote_name", label: "Quote", width: "2fr" },
                        { key: "status", label: "Status" },
                        {
                          key: "grand_total",
                          label: "Total",
                          render: (r) => <MonoText variant="button">{r.grand_total ?? "—"}</MonoText>,
                        },
                      ]}
                      rows={related.quotations || []}
                      emptyMessage="No quotes for this customer."
                      onRowClick={(r) => window.open(`/quotation/${r.id}`, "_blank")}
                    />
                  </InfoCard>
                )}

                {activeTab === "cases" && (
                  <InfoCard icon="support_agent" title="Cases">
                    <MiniTable
                      columns={[
                        {
                          key: "case_number",
                          label: "Case",
                          render: (r) => <MonoText variant="button">{r.case_number || r.id}</MonoText>,
                        },
                        { key: "subject", label: "Subject", width: "2fr" },
                        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} label={r.status} size="xs" /> },
                      ]}
                      rows={related.cases || []}
                      emptyMessage="No cases for this customer."
                      onRowClick={(r) => window.open(`/cases/${r.id}`, "_blank")}
                    />
                  </InfoCard>
                )}

                {activeTab === "conversations" && (
                  <MDBox display="flex" flexDirection="column" gap={3}>
                    <InfoCard icon="history" title="Previous calls">
                      <MiniTable
                        columns={[
                          { key: "direction", label: "Direction" },
                          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} label={r.status} size="xs" /> },
                          {
                            key: "started_at",
                            label: "When",
                            width: "1.5fr",
                            render: (r) => (
                              <MonoText variant="button">
                                {r.started_at ? new Date(r.started_at).toLocaleString() : "—"}
                              </MonoText>
                            ),
                          },
                          { key: "disposition_name", label: "Outcome" },
                          {
                            key: "media",
                            label: "Media",
                            render: (r) => (
                              <MDBox display="flex" gap={0.5}>
                                {r.has_recording && <Icon sx={{ fontSize: 16, color: "#5C6B82" }}>graphic_eq</Icon>}
                                {r.transcription_status === "completed" && (
                                  <Icon sx={{ fontSize: 16, color: "#5C6B82" }}>subject</Icon>
                                )}
                              </MDBox>
                            ),
                          },
                        ]}
                        rows={related.conversations || []}
                        emptyMessage="No previous calls with this number."
                        onRowClick={(r) => setSelectedCallId(r.id === selectedCallId ? null : r.id)}
                      />
                    </InfoCard>

                    {/* Selecting a past call opens it here rather than
                        navigating away: the agent may well be on a live call,
                        and leaving the console would drop the screen-pop. */}
                    {selectedCallId && (
                      <InfoCard icon="graphic_eq" title="Conversation & Recording">
                        <ConversationRecording conversationId={selectedCallId} />
                      </InfoCard>
                    )}
                  </MDBox>
                )}
              </>
            )}

            {/* Idle with no lookup: the console is the manual-search screen,
                not a grey "no active call" slab. */}
            {!looking && !lookup && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <InfoCard icon="search" title="Look up a number">
                    <MDBox display="flex" gap={1}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by phone number"
                        value={manualNumber}
                        onChange={(e) => setManualNumber(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && runLookup(manualNumber.trim())}
                      />
                      <MDButton
                        variant="outlined"
                        color="info"
                        startIcon={<Search />}
                        onClick={() => runLookup(manualNumber.trim())}
                      >
                        Search
                      </MDButton>
                    </MDBox>
                  </InfoCard>
                </Grid>
                <Grid item xs={12}>
                  <InfoCard icon="history" title="Recent calls">
                    <MiniTable
                      columns={[
                        {
                          key: "counterparty",
                          label: "Number",
                          width: "1.5fr",
                          render: (r) => (
                            <MonoText variant="button">
                              {r.direction === "outbound" ? r.to_address : r.from_address}
                            </MonoText>
                          ),
                        },
                        { key: "direction", label: "Direction" },
                        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} label={r.status} size="xs" /> },
                        { key: "disposition_name", label: "Outcome" },
                      ]}
                      rows={recent}
                      emptyMessage="No calls yet."
                      onRowClick={(r) =>
                        runLookup(r.direction === "outbound" ? r.to_address : r.from_address)
                      }
                    />
                  </InfoCard>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        </>
        )}
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
